import { useEffect, useMemo, useState } from 'react'
import { useCached } from '../lib/cache'
import { useLiveRows } from '../lib/live'
import { Page, TopBar } from '../components/Shell'
import { Skeleton, Empty } from '../components/Skeleton'
import { I } from '../components/Icons'
import { IntakeCard, CARD_CSS } from '../components/IntakeCard'
import { PrintCardButton } from '../components/PrintCard'
import { loadRecentIntakes } from '../lib/api'
import { cardFromRow, intakeFrom, intakeHow, isMasihOwn, type IntakeRow } from '../lib/intakeCard'
import { LABEL, fmtC } from '../lib/types'

const TZ = 'America/New_York'
const dayKey = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: TZ })
const dayLabel = (key: string) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: TZ })
  const yest = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: TZ })
  if (key === today) return 'Today'
  if (key === yest) return 'Yesterday'
  return new Date(key + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}
const time = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ })
const no = (n: number | null) => (n ? '#' + String(n).padStart(4, '0') : '#—')
const HOW = { bought: 'Bought from', consigned: 'Consigned by', traded: 'Traded in by' } as const

function dealShort(r: IntakeRow) {
  const how = intakeHow(r)
  if (how === 'traded') return `${fmtC(r.credit_cents ?? r.cost_cents)} credit`
  if (how === 'consigned') return r.split_pct != null ? `${Math.round((100 - Number(r.split_pct)) * 100) / 100}% fee` : 'Consigned'
  return fmtC(isMasihOwn(r) ? r.owner_cost_cents ?? 0 : r.cost_cents)
}

/** Intake → Recent intakes: every recorded intake, newest first, so a card that didn't print (or got lost) can be printed again. */
export function IntakeRecent() {
  const R = useCached<IntakeRow[]>('recent-intakes', () => loadRecentIntakes(), { staleMs: 0 })
  useLiveRows('intakes', '', () => { void R.refresh() })
  const rows = R.data ?? null
  const err = rows ? '' : R.error
  // 16 Oct: after a failed load (a wifi drop) the list stayed empty until the screen was reopened; keep trying instead.
  useEffect(() => {
    if (!err) return
    const t = window.setInterval(() => { void R.refresh() }, 5000)
    return () => window.clearInterval(t)
  }, [err]) // eslint-disable-line react-hooks/exhaustive-deps -- R.refresh is stable per key
  const [q, setQ] = useState('')
  const [selId, setSelId] = useState('')
  const list = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/^#/, '')
    if (!t) return rows ?? []
    return (rows ?? []).filter((r) => `${String(r.intake_no ?? '').padStart(4, '0')} ${r.brand} ${r.item} ${r.size} ${intakeFrom(r)} ${r.signed_by ?? ''}`.toLowerCase().includes(t))
  }, [rows, q])
  const groups = useMemo(() => {
    const out: { key: string; rows: IntakeRow[] }[] = []
    for (const r of list) { const k = dayKey(r.created_at); const g = out[out.length - 1]; if (g && g.key === k) g.rows.push(r); else out.push({ key: k, rows: [r] }) }
    return out
  }, [list])
  const sel = (rows ?? []).find((r) => r.id === selId) ?? list[0] ?? null
  const card = useMemo(() => (sel ? cardFromRow(sel) : null), [sel])
  return (
    <Page
      bar={<TopBar back="Intake" backTo="/intake" title="Recent intakes" caption={rows?.length ? `Last ${rows.length}` : ''} />}
      aside={
        <>
          <div className="aside__body ir-preview">
            <div className="icard-head"><b>{sel ? `Intake ${no(sel.intake_no)}` : 'Intake card'}</b><span>{sel ? `${dayLabel(dayKey(sel.created_at))}, ${time(sel.created_at)}` : 'Pick an intake'}</span></div>
            <style>{CARD_CSS}</style>
            {card && <div className="ic-preview"><IntakeCard c={card} /></div>}
            <div className="hint" style={{ padding: '10px 20px' }}>The card is rebuilt from the saved intake, the same card that printed on confirm. Print card opens the share sheet; pick the printer and Print (5×7).</div>
          </div>
          <div className="aside__foot">
            {card && sel ? <PrintCardButton card={card} className="cta" label={`Print card ${no(sel.intake_no)}`} /> : <button type="button" className="cta" disabled>Print card</button>}
          </div>
        </>
      }
    >
      {err && <div className="banner" style={{ background: '#d70015' }}>{err}. Trying again…</div>}
      <div className="search">{I.search()}<label htmlFor="irq" style={{ position: 'absolute', left: -9999 }}>Search intakes</label><input id="irq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search intake #, brand, item, name" /></div>
      {rows === null && !err && <div className="card"><Skeleton rows={5} h={64} card={false} /></div>}
      {groups.map((g) => (
        <div key={g.key}>
          <div className="section-title" style={{ fontSize: 17, padding: '10px 0 6px' }}>{dayLabel(g.key)} <span style={{ color: 'var(--caption)', fontWeight: 500 }}>· {g.rows.length}</span></div>
          <div className="card">
            {g.rows.map((r) => (
              <button key={r.id} type="button" className="person" aria-pressed={sel?.id === r.id} onClick={() => setSelId(r.id)}>
                <span className="person__initial" style={{ borderRadius: 10, fontSize: 13, width: 'auto', minWidth: 58, padding: '0 6px' }}>{no(r.intake_no)}</span>
                <span style={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
                  <span className="person__name">{r.brand} {r.item}{r.size ? ' Sz ' + r.size : ''}</span>
                  <span className="person__sub">{r.sourced_by === 'shane' ? 'Sourced by' : HOW[intakeHow(r)]} {intakeFrom(r) || 'unknown'} · {time(r.created_at)}{r.signed_by ? ` · ID ${r.signed_by}` : ''} · {LABEL.cond[r.condition as keyof typeof LABEL.cond] ?? ''}{r.rehearsal ? ' · rehearsal' : ''}</span>
                </span>
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{dealShort(r)}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {rows && list.length === 0 && <div className="card"><Empty icon={I.intake(24)} line={q.trim() ? 'No intake matches.' : 'No intakes recorded yet.'} /></div>}
    </Page>
  )
}
